"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { Menu, X, User } from "lucide-react";
import useAuth from "@/hooks/useAuth";
import { Button } from "../ui/button";
import { useMutation } from "@tanstack/react-query";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useRouter } from "next/navigation";

const navItems = [
  { name: "Home", href: "/" },
  { name: "Invest", href: "/invest" },
  // { name: "Location", href: "/location" },
  { name: "About Us", href: "https://chargemod.com/about-us" },
  { name: "Contact Us", href: "https://chargemod.com/contact-us" },
];

export default function Navbar() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const token = auth?.accessToken;
  const router = useRouter();

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosPrivate.get("/distributor-user/logout");
      return response.data;
    },
    onSuccess: () => {
      window.location.href = "/login";
      console.log("Logged out successfully");
      // Optional: Close profile on logout too
      setProfileOpen(false);
      window.location.reload();
    },
    onError: (error) => {
      console.error("Logout failed", error);
    },
  });

  const handleLogout = () => {
    console.log("Logout clicked");
    logoutMutation.mutate();
  };

  return (
    <div className="w-full z-50">
      {/* Logo */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-6 z-50 py-2 sm:py-3">
        <Link
          href="/"
          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800"
        >
          charge<span className="text-orange-400">MOD</span>
        </Link>
      </div>

      {/* Desktop Navbar */}
      <div className="hidden lg:flex fixed top-6 right-20 z-50 px-4 py-3 bg-white/70 backdrop-blur-md shadow-md rounded-2xl gap-4">
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              "text-sm font-medium transition-colors flex items-start px-6 py-1",
              pathname === item.href
                ? "text-green-600 font-semibold"
                : "text-gray-800 hover:text-green-500 hover:font-semibold"
            )}
          >
            {item.name}
          </Link>
        ))}

        {token && (
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="w-10 h-8 cursor-pointer rounded-full bg-orange-500 flex items-center justify-center text-white hover:bg-orange-600 transition"
            >
              <User size={20} />
            </button>

            {profileOpen && (
              <div
                className="
        absolute right-0 mt-2 w-36
        bg-white shadow-xl rounded-lg
        py-4 px-3
        z-50
        transform origin-top-right
        animate-dropdown
      "
              >
                {/* --- CHANGE MADE HERE --- */}
                <Link
                  href="/invest/my-details"
                  onClick={() => setProfileOpen(false)}
                >
                  <Button
                    className="
            w-full
            text-left
            text-sm
            text-gray-700
            hover:bg-black
            hover:text-white
            cursor-pointer
            rounded-md
            transition
            duration-200
          "
                    variant="ghost"
                  >
                    My Details
                  </Button>
                </Link>
                {/* ------------------------ */}

                <Button
                  onClick={handleLogout}
                  className="
          w-full
          text-left
          text-sm
          text-white
            cursor-pointer      
          px-4 py-2
          rounded-md
          mt-2
          transition
          duration-200
        "
                  variant="destructive"
                >
                  Logout
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-6 right-6 z-50">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 bg-white/80 rounded-md shadow-md"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Mobile Slide Menu Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 transition-all duration-300 ease-in-out",
          menuOpen ? "bg-black/30" : "pointer-events-none opacity-0"
        )}
        onClick={() => setMenuOpen(false)}
      />

      {/* Slide-in Drawer */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out",
          menuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Close Button */}
        <div className="flex justify-between items-center p-4 border-b border-orange-100">
          <h2 className="text-xl font-bold text-gray-800">
            charge<span className="text-orange-400">MOD</span>
          </h2>
          <button onClick={() => setMenuOpen(false)}>
            <X size={24} className="text-orange-500" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex flex-col py-4 px-6 space-y-4">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className={cn(
                "text-base font-medium px-3 py-2 rounded-md transition",
                pathname === item.href
                  ? "text-green-600 font-semibold"
                  : "text-gray-800 hover:text-green-600 hover:bg-green-50"
              )}
            >
              {item.name}
            </Link>
          ))}

          {token && (
            <Link
              href="/invest/my-details"
              onClick={() => setMenuOpen(false)}
              className="text-base font-medium px-3 py-2 rounded-md text-gray-800 hover:text-green-600 hover:bg-green-50"
            >
              My Details
            </Link>
          )}

          {token && (
            <button
              onClick={() => {
                handleLogout();
                setMenuOpen(false);
              }}
              className="mt-10 w-full px-3 py-2 rounded-md bg-orange-500 text-white font-medium hover:bg-orange-600"
            >
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}